import { calculateHeaderGroupHeight } from '@theme/critical';
import { Component } from '@theme/component';
import { onDocumentLoaded, changeMetaThemeColor } from '@theme/utilities';

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */
class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

  /**
   * Width of window when header drawer was hidden
   * @type {number | null}
   */
  #menuDrawerHiddenWidth = null;

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * Whether the header has been scrolled offscreen, when sticky behavior is 'scroll-up'
   * @type {boolean}
   */
  #offscreen = false;

  /**
   * The last recorded scrollTop of the document, when sticky behavior is 'scroll-up'
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * A timeout to allow for hiding animation, when sticky behavior is 'scroll-up'
   * @type {number | null}
   */
  #timeout = null;

  /**
   * The duration to wait for hiding animation, when sticky behavior is 'scroll-up'
   * @constant {number}
   */
  #animationDelay = 150;

  /**
   * requestAnimationFrame id for scroll work.
   * @type {number | null}
   */
  #scrollRaf = null;

  /**
   * Latest measured header height.
   * @type {number}
   */
  #headerHeight = 0;

  /**
   * Latest applied header height css value.
   * @type {string}
   */
  #lastHeaderHeightValue = '';

  /**
   * Sets a data attribute only when the value actually changes.
   *
   * @param {string} key
   * @param {string} value
   */
  #setDatasetValue(key, value) {
    if (this.dataset[key] !== value) {
      this.dataset[key] = value;
    }
  }

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume.
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry) return;

    const height = Math.round(entry.borderBoxSize?.[0]?.blockSize ?? entry.contentRect.height ?? 0);
    const value = `${height}px`;

    this.#headerHeight = height;

    if (value !== this.#lastHeaderHeightValue) {
      this.#lastHeaderHeightValue = value;
      document.body.style.setProperty('--header-height', value);
    }

    if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
      this.#updateMenuVisibility(false);
    }
  });

  /**
   * Observes the header while scrolling the viewport to track when it's actively sticky.
   *
   * @param {boolean} alwaysSticky - Determines if we need to observe when the header is offscreen.
   */
  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    const config = {
      threshold: alwaysSticky ? 1 : 0,
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;

      if (alwaysSticky) {
        this.#setDatasetValue('stickyState', isIntersecting ? 'inactive' : 'active');
        changeMetaThemeColor(this.refs.headerRowTop);
      } else {
        this.#offscreen = !isIntersecting || this.dataset.stickyState === 'active';
      }
    }, config);

    this.#intersectionObserver.observe(this);
  };

  /**
   * Handles the overflow minimum event from the header menu.
   *
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  /**
   * Updates the visibility of the menu and drawer.
   *
   * @param {boolean} hideMenu - Whether to hide the menu and show the drawer.
   */
  #updateMenuVisibility(hideMenu) {
    const { headerDrawerContainer, headerMenu } = this.refs;

    if (hideMenu) {
      if (headerDrawerContainer.classList.contains('desktop:hidden')) {
        headerDrawerContainer.classList.remove('desktop:hidden');
      }

      this.#menuDrawerHiddenWidth = window.innerWidth;

      if (!headerMenu.classList.contains('hidden')) {
        headerMenu.classList.add('hidden');
      }

      return;
    }

    if (!headerDrawerContainer.classList.contains('desktop:hidden')) {
      headerDrawerContainer.classList.add('desktop:hidden');
    }

    this.#menuDrawerHiddenWidth = null;

    if (headerMenu.classList.contains('hidden')) {
      headerMenu.classList.remove('hidden');
    }
  }

  /**
   * Handles scroll work, throttled through requestAnimationFrame.
   */
  #handleWindowScroll = () => {
    if (this.#scrollRaf !== null) return;

    this.#scrollRaf = window.requestAnimationFrame(() => {
      this.#scrollRaf = null;
      this.#updateStickyStateOnScroll();
    });
  };

  /**
   * Updates sticky/header state based on scroll.
   */
  #updateStickyStateOnScroll() {
    const stickyMode = this.getAttribute('sticky');

    if (!this.#offscreen && stickyMode !== 'always') return;

    const scrollTop = document.scrollingElement?.scrollTop ?? window.scrollY ?? 0;
    const isScrollingUp = scrollTop < this.#lastScrollTop;
    const isAtTop = scrollTop <= 1;

    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    if (stickyMode === 'always') {
      if (isAtTop) {
        this.#setDatasetValue('scrollDirection', 'none');
      } else if (isScrollingUp) {
        this.#setDatasetValue('scrollDirection', 'up');
      } else {
        this.#setDatasetValue('scrollDirection', 'down');
      }

      this.#lastScrollTop = scrollTop;
      return;
    }

    if (isScrollingUp) {
      this.removeAttribute('data-animating');

      if (isAtTop) {
        this.#offscreen = false;
        this.#setDatasetValue('stickyState', 'inactive');
        this.#setDatasetValue('scrollDirection', 'none');
      } else {
        this.#setDatasetValue('stickyState', 'active');
        this.#setDatasetValue('scrollDirection', 'up');
      }
    } else if (this.dataset.stickyState === 'active') {
      this.#setDatasetValue('scrollDirection', 'none');
      this.setAttribute('data-animating', '');

      this.#timeout = window.setTimeout(() => {
        this.#setDatasetValue('stickyState', 'idle');
        this.removeAttribute('data-animating');
        this.#timeout = null;
      }, this.#animationDelay);
    } else {
      this.#setDatasetValue('scrollDirection', 'none');
      this.#setDatasetValue('stickyState', 'idle');
    }

    this.#lastScrollTop = scrollTop;
  }

  connectedCallback() {
    super.connectedCallback();

    this.#resizeObserver.observe(this);
    this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);

    const stickyMode = this.getAttribute('sticky');

    if (stickyMode) {
      this.#observeStickyPosition(stickyMode === 'always');

      if (stickyMode === 'scroll-up' || stickyMode === 'always') {
        document.addEventListener('scroll', this.#handleWindowScroll, { passive: true });
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    document.removeEventListener('scroll', this.#handleWindowScroll);

    if (this.#scrollRaf !== null) {
      window.cancelAnimationFrame(this.#scrollRaf);
      this.#scrollRaf = null;
    }

    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    document.body.style.setProperty('--header-height', '0px');
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

onDocumentLoaded(() => {
  const header = document.querySelector('#header-component');
  const headerGroup = document.querySelector('#header-group');

  if (!headerGroup) return;

  let resizeFrame = null;
  const observedChildren = new WeakSet();

  const scheduleHeaderGroupHeightCalculation = () => {
    if (resizeFrame !== null) return;

    resizeFrame = window.requestAnimationFrame(() => {
      resizeFrame = null;
      calculateHeaderGroupHeight(header, headerGroup);
    });
  };

  const resizeObserver = new ResizeObserver(scheduleHeaderGroupHeightCalculation);

  const observeHeaderGroupChildren = () => {
    const children = headerGroup.children;

    for (let i = 0; i < children.length; i++) {
      const element = children[i];

      if (element === header || !(element instanceof HTMLElement) || observedChildren.has(element)) continue;

      observedChildren.add(element);
      resizeObserver.observe(element);
    }
  };

  observeHeaderGroupChildren();
  scheduleHeaderGroupHeightCalculation();

  const mutationObserver = new MutationObserver((mutations) => {
    let shouldObserveChildren = false;

    for (const mutation of mutations) {
      if (mutation.type === 'childList') {
        shouldObserveChildren = true;
        break;
      }
    }

    if (!shouldObserveChildren) return;

    observeHeaderGroupChildren();
    scheduleHeaderGroupHeightCalculation();
  });

  mutationObserver.observe(headerGroup, { childList: true });
});